import { ausrehabApi as baseApi } from '../baseApi';
import type { PricingDiscountRule } from '../../utils/pricingCalculator';
import type { BulkRequestProps, RequestAssetProps, PaymentSummaryProps } from '../../models';
import type { PrebillingInvoiceResponse, GetBulkRequestsResponseData } from './apis.types';

const bulkRequestApi = baseApi.injectEndpoints({
	endpoints: (builder) => ({
		getBulkRequests: builder.query<BulkRequestProps[], void>({
			query: () => ({
				url: 'bulk-requests',
			}),
			providesTags: ['Bulk-Request'],
		}),

		getBulkRequestAssignment: builder.query<GetBulkRequestsResponseData[], void>({
			query: () => ({
				url: 'bulk-requests/assignments',
			}),
			providesTags: ['Bulk-Request'],
		}),

		updateBulkRequest: builder.mutation({
			query: (payload) => {
				return {
					url: `bulk-requests/${payload.id}`,
					method: 'PUT',
					body: payload,
				};
			},
			invalidatesTags: ['Bulk-Request'],
		}),

		updateBulkRequestStatus: builder.mutation({
			query: (payload) => {
				return {
					url: `bulk-requests/${payload.id}/status`,
					method: 'PUT',
					body: {
						status: payload.status,
					},
				};
			},
			invalidatesTags: ['Bulk-Request'],
		}),

		cancelBulkRequest: builder.mutation({
			query: (id) => {
				return {
					url: `bulk-requests/${id}/cancel`,
					method: 'DELETE',
				};
			},
			invalidatesTags: ['Bulk-Request'],
		}),

		delBulkRequest: builder.mutation({
			query: (id) => {
				return {
					url: `bulk-requests/${id}`,
					method: 'DELETE',
				};
			},
			invalidatesTags: ['Bulk-Request'],
		}),

		updateBulkRequestAssessmentDates: builder.mutation({
			query: (payload) => {
				return {
					url: `bulk-requests/${payload.id}/assessmentDates`,
					method: 'PUT',
					body: {
						assessmentDates: payload.assessmentDates,
					},
				};
			},
			invalidatesTags: ['Bulk-Request'],
		}),

		setBulkRequestAdminNote: builder.mutation({
			query: (payload) => {
				return {
					url: `bulk-requests/${payload.id}/adminNote`,
					method: 'PUT',
					body: {
						adminNote: payload.note,
					},
				};
			},
			invalidatesTags: ['Bulk-Request'],
		}),

		setBulkRequestAccountNote: builder.mutation({
			query: (payload) => {
				return {
					url: `bulk-requests/${payload.id}/accountNote`,
					method: 'PUT',
					body: {
						accountNote: payload.note,
					},
				};
			},
			invalidatesTags: ['Bulk-Request'],
		}),

		addBulkRequest: builder.mutation({
			query: (payload) => {
				payload.id = undefined;

				return {
					url: 'bulk-requests',
					method: 'POST',
					body: payload,
				};
			},
			invalidatesTags: ['Bulk-Request'],
		}),

		payBulkRequest: builder.mutation({
			query: (payload: Pick<BulkRequestProps, 'id' | 'amount'>) => {
				const { id, amount } = payload;

				return {
					url: `bulk-requests/${id}/pay`,
					method: 'POST',
					body: {
						payAmount: amount,
					},
				};
			},
		}),

		generateBulkPrebillingInvoice: builder.mutation<PrebillingInvoiceResponse, number | string>({
			query: (id) => ({
				url: `bulk-requests/${id}/invoice`,
				method: 'POST',
			}),
			invalidatesTags: ['Bulk-Request'],
		}),

		getBulkRequest: builder.query<BulkRequestProps, string>({
			query: (id) => ({ url: `bulk-requests/${id}` }),
			providesTags: ['Bulk-Request'],
		}),

		getBulkRequestPaymentSummary: builder.query<PaymentSummaryProps, string>({
			query: (id) => ({
				url: `bulk-requests/${id}/paymentSummary`,
			}),
			providesTags: ['Bulk-Request'],
		}),

		/*
		 * Reports
		 */
		getBulkRequestReports: builder.query<RequestAssetProps[], number>({
			query: (id) => ({
				url: `bulk-requests/${id}/reports`,
			}),
			providesTags: ['Bulk-Request-Reports'],
		}),

		uploadBulkRequestReport: builder.mutation({
			query: (payload) => {
				const { requestId, reports } = payload;

				return {
					url: `bulk-requests/${requestId}/reports`,
					method: 'POST',
					body: {
						reports,
					},
				};
			},
			invalidatesTags: ['Bulk-Request-Reports'],
		}),

		deleteBulkRequestReport: builder.mutation({
			query: (payload) => {
				const { requestId, reportName } = payload;

				return {
					url: `bulk-requests/${requestId}/reports`,
					method: 'DELETE',
					params: {
						report_name: reportName,
					},
				};
			},
			invalidatesTags: ['Bulk-Request-Reports'],
		}),

		getBulkRequestReport: builder.query<RequestAssetProps, { requestId: number; reportName: string; action?: string }>({
			query: (args) => {
				const { requestId, reportName, action } = args;
				return {
					url: `bulk-requests/${requestId}/reports/${reportName}`,
					method: 'HEAD',
					params: {
						action,
					},
				};
			},
			transformResponse: (response: RequestAssetProps, meta: { response: Response }) => {
				return {
					...response,
					uri: meta.response.headers.get('request-asset-uri') ?? '',
				};
			},
			providesTags: ['Bulk-Request'],
		}),

		getBulkTestDiscountRule: builder.query<PricingDiscountRule[], void>({
			query: () => ({
				url: 'bulk-requests/discount-rules',
			}),
		}),

		assignBulkRequest: builder.mutation<void, { requestIds: number[]; userId: number }>({
			query: ({ requestIds, userId }) => ({
				url: 'bulk-requests/assign',
				method: 'POST',
				body: { requestIds, userId },
			}),
			invalidatesTags: ['Bulk-Request'],
		}),

		unassignBulkRequest: builder.mutation<void, { requestIds: number[] }>({
			query: ({ requestIds }) => ({
				url: 'bulk-requests/assign',
				method: 'DELETE',
				body: { requestIds },
			}),
			invalidatesTags: ['Bulk-Request'],
		}),
	}),
	overrideExisting: false,
});

export const {
	useGetBulkRequestsQuery,
	useGetBulkRequestAssignmentQuery,
	useGetBulkRequestQuery,
	useLazyGetBulkRequestQuery,
	useGetBulkRequestReportsQuery,
	useGetBulkRequestPaymentSummaryQuery,
	useLazyGetBulkRequestPaymentSummaryQuery,
	useLazyGetBulkRequestReportQuery,
	useGetBulkTestDiscountRuleQuery,

	// mutation
	useAddBulkRequestMutation,
	usePayBulkRequestMutation,
	useGenerateBulkPrebillingInvoiceMutation,
	useDelBulkRequestMutation,
	useUpdateBulkRequestMutation,
	useUpdateBulkRequestStatusMutation,
	useUpdateBulkRequestAssessmentDatesMutation,
	useUploadBulkRequestReportMutation,
	useDeleteBulkRequestReportMutation,
	useCancelBulkRequestMutation,
	useSetBulkRequestAdminNoteMutation,
	useSetBulkRequestAccountNoteMutation,
	useAssignBulkRequestMutation,
	useUnassignBulkRequestMutation,
} = bulkRequestApi;
